import { OpenIDConfiguration } from './model/open-id-configuration';
import { HttpClient } from '../http/http-client/http-client';
import { HttpResponse } from '../http/http-response/http-response';
import { Logger } from '../../logger/logger';
import { HttpRequestInit } from '../http/http-request/http-request';
import { UtilWorker } from '../worker/util-worker';
import { proxy, wrap } from 'comlink';
import { UserInfo } from './model/user-info';
import { CXoneConfiguration } from './model/cxone-configuration';
import { StorageKeys } from '../../constants/storage-key'
import { HttpUtilService } from '../http/http-util-service';
import { ApiParserAuth } from '../../util/api-parser-auth'
import { LocalStorageHelper } from '../../util/storage-helper-local';
import { AuthState, AuthToken } from "@nice-ccf/common-sdk";
import { WhoamiResponse } from '@nice-ccf/core-sdk';

const TOKEN_EXPIREIN_BUFFER_IN_SECONDS = 600; // value is in seconds
/**
 * Class to perform common authentication of user
 */
export abstract class CXoneAuthBase {
  private authWorker: any;
  private authToken = <AuthToken>{};
  private cxOneConfig =  <CXoneConfiguration>{};
  public userInfo = <UserInfo>{};
  protected logger: Logger = new Logger('SDK', 'CXoneAuthBase');
  protected utilService: HttpUtilService = new  HttpUtilService();
  apiParserAuth = new ApiParserAuth();
  static cxoneAuth: CXoneAuthBase;

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const authObject = CXoneAuthBase.instace();
   * ```
   */
  public static get Instance(): CXoneAuthBase{
    return CXoneAuthBase.cxoneAuth;
  }

  /**
   * Method to get discovery endpoints for authentication purposes
   * @returns - Http Response form well known openid config api
   * ```
   * @example
   * getOpenIDConfiguration('https://cxone.dev.niceincontact.com')
   * ```
   */
  public getOpenIDConfiguration(hostname: string): Promise<OpenIDConfiguration> {
    return new Promise<OpenIDConfiguration>((resolve, reject) => {
      const openIDConfigFromStorage = LocalStorageHelper.getItem(StorageKeys.OIDC_CONFIG);

      if (!openIDConfigFromStorage) {
        const endpoint = hostname + '/.well-known/openid-configuration';
      const reqInit = this.utilService.initHeader('', 'application/json');

      HttpClient.get(endpoint, reqInit).then(
        (resp: HttpResponse) => {
          const discoverAuthResponse = this.apiParserAuth.parseOpenIDConfigResponse(resp);
          LocalStorageHelper.setItem(StorageKeys.OIDC_CONFIG,discoverAuthResponse);
          this.logger.info('getOpenIDConfiguration', 'Open ID configuration successfully generated');
          resolve(discoverAuthResponse);
        },
        (err: HttpResponse) => {
          this.logger.error('getOpenIDConfiguration', err.toString());
          reject(err);
        }
      );
      }
      else {
        resolve(JSON.parse(openIDConfigFromStorage));
      }
    });
  }

  /**
   *
   * This method is to get CXone Configurations for authentication purpose 
   * @returns - Http Response from well known cxone config api
   * 
   * @param hostName - hostName
   * @param tenantId - tenantId
   * ```
   * @example
   * getCXoneConfiguration('https://cxone.dev.niceincontact.com','11e85da0-f32c-7e10-898c-0242ac110003');
   * ```
   */
  public getCXoneConfiguration(hostname: string, tenantId: string) :Promise<CXoneConfiguration>{
    return new Promise<CXoneConfiguration>((resolve, reject) => {
      const endpoint =
        hostname + '/.well-known/cxone-configuration?tenantId=' + tenantId;
      const reqInit = this.utilService.initHeader('', 'application/json');
      HttpClient.get(endpoint, reqInit).then(
        (resp: HttpResponse) => {
          const resourceUrlResponse = this.apiParserAuth.parseCXoneConfiguration(resp);
          this.setCXoneConfig(resourceUrlResponse);
          LocalStorageHelper.setItem(StorageKeys.CXONE_CONFIG,resourceUrlResponse);
          this.logger.info('getCXoneConfiguration', 'CXone configuration successfully generated');
          resolve(resourceUrlResponse);
        },
        (err: HttpResponse) => {
          this.logger.error('getCXoneConfiguration', err.toString());
          reject(err);
        }
      );
    });
  }


  /**
   * Method to get the whoami api response
   * @returns - Http Response from whoAmI api
   * @param authToken - 'authToken'
   * ```
   * @example
   * getWhoAmIData(authToken)
   * ```
   */
   public getWhoAmIData(authToken:AuthToken) :Promise<WhoamiResponse>{
    return new Promise<WhoamiResponse>((resolve, reject) => {
      const decodedIdToken = JSON.parse(atob(authToken.idToken.split('.')[1]));
      const icClusterId = decodedIdToken.icClusterId;
      const  domain = decodedIdToken.icDomain;
      const  acdAuthTokenEndPoint = this.getAcdAuthorizationTokenEndpoint(icClusterId, domain);
      if (acdAuthTokenEndPoint) {
        let acdAuthTokenUrl=''
        acdAuthTokenUrl = acdAuthTokenEndPoint +'/whoami';
        const reqInit: HttpRequestInit = {
          headers: this.utilService.initHeader('', 'application/json').headers,
          body: {
            token: authToken.accessToken
          }
        };
        HttpClient.post(acdAuthTokenUrl,reqInit).then(
          (resp: HttpResponse) =>{
          const whoAmIResponse = this.apiParserAuth.parseWhoamiResponse(resp);
          resolve(whoAmIResponse);
        },
        (error: HttpResponse) => {
          this.logger.error('getAccessToken', error.toString());
          reject(error);
        })
      } 
      else {
        this.logger.error('getAuthTokenEndPoint', 'Token endpoint is null');
      }
    })
  }

  /**
   *
   * This method is to set the endpoints for user hub and central systems
   * ```
   * @example
   * setAuthEndPointsForUserHub();
   * ```
   */
  public setAuthEndPoints(authToken:AuthToken){
        const decodedIdToken = JSON.parse(atob(authToken.idToken.split('.')[1]));
        const decodedAccessToken = JSON.parse(atob(authToken.accessToken.split('.')[1]));
        const icClusterId = decodedIdToken.icClusterId;
        let domain = decodedIdToken.icDomain;
        this.cxOneConfig.isUserHub = false;
        if(decodedAccessToken.tenant){ // Check for User Hub
          const userHubArea = decodedIdToken.area;
          domain = this.getUserHubDomain(icClusterId, userHubArea);
          this.cxOneConfig.isUserHub = true;
          this.cxOneConfig.wfmUri = this.getUserHubWfmUri(userHubArea, domain);
          this.cxOneConfig.notificationUri = this.getUserHubNotificationUri(userHubArea, domain);
          this.cxOneConfig.userHubBaseUrl=this.getUserHubWfmUri(userHubArea, domain);
        }
       LocalStorageHelper.setItem(StorageKeys.CXONE_CONFIG,this.cxOneConfig);
  }

  /**
   *
   * This method is to get the userHubDomain according to the cluster Id
   * @returns - userHubDomain
   * 
   * @param clusterId - icClusterId
   * @param userHubArea - userHubArea
   * ```
   * @example
   * getUserHubDomain('DO31','na1');
   * ```
   */
  private getUserHubDomain(clusterId:string, userHubArea:string) {
    let userHubDomain = 'nice-incontact.com';
    clusterId = clusterId.toUpperCase();
    if (userHubArea === 'na1') {
      // Below are the conditions are Staging, Testing, Dev Environments
      if ((clusterId.startsWith('SC')
        || clusterId.startsWith('SO'))) {
        userHubDomain = 'staging.nice-incontact.com';
      } else if ((clusterId.startsWith('TC')
        || clusterId.startsWith('TO')
        || clusterId.startsWith('TV'))) {
        userHubDomain = 'test.nice-incontact.com';
      } else if ((clusterId.startsWith('DO')
        || clusterId.startsWith('HC'))) {
        userHubDomain = 'dev.nice-incontact.com';
      }
    } else if (userHubArea === 'na3') {
      userHubDomain = 'nicecxone-gov.com';
    } else if (userHubArea === 'na1' || userHubArea === 'au1') {
      userHubDomain = 'nice-incontact.com';
    } else if (userHubArea === 'na2'
      || userHubArea === 'eu1'
      || userHubArea === 'uk1'
      || userHubArea === 'ca1'
      || userHubArea === 'ja1') {// london-uk1, canada-ca1, japan-ja1 region
      userHubDomain = 'niceincontact.com';
    }
    return userHubDomain;
  }
    
  /**
   *
   * This method returns the Authorization Token Endpoint
   * @returns - api end point
   * 
   * @param icClusterId - icClusterId
   * @param domain - domain
   * ```
   * @example
   * getAcdAuthorizationTokenEndpoint('SC11','ucnlabext.com');
   * ```
   */
  private getAcdAuthorizationTokenEndpoint(icClusterId:string, domain:string) {
    return 'https://api-' + icClusterId + '.' + domain + '/incontactauthorizationserver/token';
  }

 /**
   *
   * This method returns the  wfm_uri
   * @returns - api end point
   * 
   * @param userHubArea - userHubArea
   * @param domain - domain
   * ```
   * @example
   * getUserHubWfmUri('na1','staging.nice-incontact.com');
   * ```
   */
   private getUserHubWfmUri(userHubArea:string, domain:string) {
    return "https://" + userHubArea + "." + domain 
  }

   /**
   *
   * This method returns the notification_uri
   * @returns - api end point
   * 
   * @param userHubArea - userHubArea
   * @param domain - domain
   * ```
   * @example
   * getUserHubNotificationUri('na1','staging.nice-incontact.com');
   * ```
   */
    private getUserHubNotificationUri(userHubArea:string, domain:string) {
      return "https://" + userHubArea + "-ws." + domain
    }

  /**
   * Method used to parse the auth token and store the values to local storage
   * @param response - authToken response
   * @param setUserInfo - flag to decide whether to set user info or not
   * @returns - parsed authToken data
   * @example -
   * ```
   * parseAndSaveAuthToken('response');
   * ```
   */
    public parseAndSaveAuthToken(authToken: HttpResponse, setUserInfo = true): AuthToken {
      const cxOneAuthToken = this.apiParserAuth.parseAuthToken(authToken);
      this.setAuthDataAndTriggerRefreshFlow(cxOneAuthToken, setUserInfo);
      LocalStorageHelper.setItem(StorageKeys.AUTH_TOKEN,cxOneAuthToken);
      return cxOneAuthToken;
    }

    /**
   * Method used to process parsed auth token and trigger refresh token flow
   * @param authToken - parsed authToken
   * @param setUserInfo - flag to decide whether to set user info or not
   * @returns - parsed authToken data
   * @example -
   * ```
   * processAuthToken('authToken');
   * ```
   */
  public setAuthDataAndTriggerRefreshFlow(authToken: AuthToken, setUserInfo = true) {
    this.setAuthToken(authToken);
    if (setUserInfo) this.setUserDetails(authToken); // if refresh token flow then we will skip updating user details part
    this.startRefreshTokenCheck(authToken);
  }

  /**
   * Used to start the check for refresh token
   * Here, first we will initiate the worker which have a timeout, this timeout will get triggered based on the expiry time of the token we have passed
   * Once the expiry time is reached the worker will execute the callback to get the refresh token that we have passed
   * @param authToken - token object which will have refresh token and the expire in detail required to obtain the new token
   * @example - startRefreshTokenCheck(authToken)
   */
  public startRefreshTokenCheck(authToken: AuthToken) {
    try {
      if (authToken) {
        this.authToken = authToken;
        const { startTimeout } = this.initUtilWorker(); // to initiate the util worker and get the methods available in this case 'startTimeout'
        // timout interval for expire token is calculated based on the expireIn - EXPIRE_IN_BUFFER( 5 min) - the time passed since the old token is received till now
        const timeoutInterval: number = ((authToken.expiresIn - TOKEN_EXPIREIN_BUFFER_IN_SECONDS) * 1000) - (new Date().getTime() - authToken.accessTokenTime);
        startTimeout(timeoutInterval, proxy(this.getRefreshToken.bind(this)));
      }
    } catch (error) {
      this.logger.error('startRefreshTokenCheck', 'error in try catch');
    }
  }

  /**
   * Used to fetch the new access token when the old token is expired
   * the authentication end point we will get from localstorage object 'discovery_response'
   */
  private async getRefreshToken() {
    try {
      this.terminateUtilWorker(); // terminate the util worker after the request has been completed
      const discoveryResponse: OpenIDConfiguration = LocalStorageHelper.getItem(StorageKeys.OIDC_CONFIG,true);

      if (discoveryResponse) {
        const reqInit: HttpRequestInit = {
          headers: this.utilService.initHeader('', 'application/json').headers,
          body: `grant_type=refresh_token&scope=openid&refresh_token=${this.authToken.refreshToken}`,
        };
        await HttpClient.post(
          discoveryResponse['tokenEndpoint'],
          reqInit
        ).then(
          (response: HttpResponse) => {
            this.parseAndSaveAuthToken(response, false);
            this.logger.info('getRefreshToken', 'refresh token api response');
          },
          (error: HttpResponse) => {
            this.logger.error('getRefreshToken', 'refresh token api error');
          }
        );
      }
    } catch (error) {
      this.logger.error('getRefreshToken', 'error in try catch');
    }
  }

  /**
   * Use to initializing the util worker and will return the method inside the worker
   * @example
   * ```
   * this.initUtilWorker();
   * ```
   */
  public initUtilWorker() {
    if (!this.authWorker)
      this.authWorker = new Worker('../worker/util-worker.ts', {
        name: 'cxone-util-worker',
        type: 'module',
      });
    return wrap<UtilWorker>(this.authWorker);
  }

  /**
   * Use to terminate the util worker instance
   * @example
   * ```
   * this.terminateUtilWorker();
   * ```
   */
  public terminateUtilWorker() {
    this.authWorker.terminate();
    this.authWorker = undefined;
  }

  /**
   * Set the userinfo from the idToken after the successful authentication
   * @param token - authtoken response
   * ```
   * @example
   * setUserDetails(authToken);
   * ```
   */
  private setUserDetails(authToken: AuthToken) {
    if (authToken) {
      const user = JSON.parse(atob(authToken.idToken.split('.')[1]));
      if (user) {
        this.userInfo.tenantId = user.tenantId;
        this.userInfo.firstName = user.given_name;
        this.userInfo.lastName = user.family_name;
        this.userInfo.icAgentId = user.icAgentId;
        this.userInfo.userName = user.name;
        this.userInfo.icBUId = user.icBUId;
        this.userInfo.icClusterId = user.icClusterId;
        this.userInfo.userId = user.userId;
      }
    }
  }

  /**
   * Get the user details from the auth token that stored in the local storage
   * @returns - It returns the user information
   * ```
   * @example
   * getUserDetails();
   * ```
   */
  public getUserDetails() {
    if (Object.keys(this.userInfo).length === 0) {
      const authToken = JSON.parse(localStorage.getItem(StorageKeys.AUTH_TOKEN) || '');
      if (authToken) {
        this.setUserDetails(authToken);
      }
    }
    return this.userInfo;
  }

  /**
   *
   * @param authToken - authTokenOject
   * ```
   * @example
   * this.setAuthToken(authToken);
   * ```
   */
  setAuthToken(authToken: AuthToken) {
    this.authToken = authToken;
  }

  /**
   *
   * @returns - returns auth token object
   * ```
   * @example
   * const authToken = this.getAuthToken();
   * ```
   */
  getAuthToken(): AuthToken {
    return this.authToken;
  }

  /**
   *
   * @param cxoneConfig - cxone configuration object
   * ```
   * @example
   * setConfig(cxoneConfig: CXoneConfiguration);
   * ```
   */
  setCXoneConfig(cxoneConfig: CXoneConfiguration) {
    this.cxOneConfig = cxoneConfig;
  }


  /**
   *
   * @returns - cxone configuration object
   * ```
   * @example
   * const cxoneConfig = this.getCXoneConfig();
   * ```
   */
  getCXoneConfig(): CXoneConfiguration {
    return this.cxOneConfig;
  }

  /**
   * Checks if user is logged in and have valid token
   * @returns - user login details with auth token
   * @example
   * ```
   * const userLoginDetails = CXoneAuthBase.getAuthState();
   * ```
   */
   public static getAuthState(): AuthState {
    const authState = <AuthState>{};
    const authToken = LocalStorageHelper.getItem(StorageKeys.AUTH_TOKEN, true);
    if (authToken) {
      const now = new Date().getTime();
      const expiry = authToken.accessTokenTime + authToken.expiresIn * 1000;
      if (now > expiry) {
        authState.isTokenValid = false;
      } else {
        authState.isTokenValid = true;
        authState.authToken = authToken;
      }
    } else {
      authState.isTokenValid = false;
    }
    return authState;
  }
}
